/**
 * Realloc — For Organizations landing page.
 *
 * Mirrors the homepage dark editorial aesthetic (serif display, mono section numbers,
 * bordered panels, white-on-black). All photographic imagery uses Unsplash CDN
 * placeholders graded dark via overlay gradients — these are intended to be swapped
 * for Realloc's real brand assets at publish time (see /app/memory/PRD.md image_assets).
 *
 * Route: /organizations (mounted in App.js).
 * Public route — no auth required.
 */
import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
    ArrowRight, ChevronDown, Cpu, Briefcase, RefreshCw, Handshake, Ruler,
    Coins, LogOut, Award, Star,
} from "lucide-react";

// ── Helpers ──
function Reveal({ children, delay = 0, className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    return (
        <motion.div
            ref={ref}
            className={className}
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.div>
    );
}

// Curated dark-graded Unsplash photos. Swap these for Realloc-owned brand imagery.
const IMG = {
    hero: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=2400&q=80&auto=format&fit=crop",          // team at modern office
    presenter: "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?w=1600&q=80&auto=format&fit=crop", // instructor presenting
    mission: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=2400&q=80&auto=format&fit=crop",   // person walking, golden hour
    portrait1: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=900&q=80&auto=format&fit=crop",  // young man
    portrait2: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&q=80&auto=format&fit=crop",  // mid-career woman
    portrait3: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=900&q=80&auto=format&fit=crop",  // woman w/ glasses
    avatars: [
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=160&q=80&auto=format&fit=crop&crop=faces",
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=160&q=80&auto=format&fit=crop&crop=faces",
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=160&q=80&auto=format&fit=crop&crop=faces",
        "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=160&q=80&auto=format&fit=crop&crop=faces",
    ],
};

const LOGO_NAMES = ["Coinbase", "Deloitte", "EY", "Google", "Kearney", "LEK", "LinkedIn", "McKinsey & Company", "Meta"];

const PILLARS = [
    { num: "01", label: "Training", title: "Upskill your people", body: "The AI Builder Program teaches your team to ship real agents and workflows. Graduates routinely 5–20x their impact." },
    { num: "02", label: "Services", title: "Build your AI infrastructure", body: "We scope, build, and run agentic workflows and AI-native systems inside your business." },
    { num: "03", label: "Talent", title: "Hire AI-native operators", body: "Proven builders, assessed on real work, ready to join your team full-time." },
];

const ACCORDION = [
    { title: "Run a cohort training", body: "Structured AI Builder cohorts led by experts, with your team learning alongside peers." },
    { title: "Embed a Realloc team", body: "We scope, build, and run agentic workflows and AI-native systems inside your business." },
    { title: "Hire AI-native operators", body: "Proven builders, assessed on real work, ready to join your team full-time." },
    { title: "Access the content library", body: "On-demand programs, playbooks, and templates for every role." },
];

const CAPABILITIES = [
    { Icon: Cpu, label: "Build with AI" },
    { Icon: Briefcase, label: "Break into AI careers" },
    { Icon: RefreshCw, label: "AI transformation" },
    { Icon: Handshake, label: "Management consulting" },
    { Icon: Ruler, label: "Product management" },
    { Icon: Coins, label: "Private equity" },
    { Icon: LogOut, label: "Outplacement" },
    { Icon: Award, label: "Career coaching" },
];


// ── Page ──
export default function OrganizationsPage() {
    useEffect(() => {
        document.title = "Realloc for Organizations — Make your organization AI-native";
    }, []);

    return (
        <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="organizations-page">
            <OrgNav />
            <Hero />
            <Pillars />
            <LogoStrip />
            <MetricsBand />
            <Programs />
            <Capabilities />
            <Mission />
            <PortraitTrio />
            <CtaBand />
            <OrgFooter />
        </div>
    );
}

// ── Nav ──
function OrgNav() {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 80);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return (
        <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/[0.08]" : "bg-transparent"}`} data-testid="org-nav">
            <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
                <Link to="/" className="font-display text-xl text-white tracking-tight" data-testid="org-nav-logo">Realloc</Link>
                <div className="flex items-center gap-4">
                    <Link to="/login" className="hidden sm:inline text-sm text-gray-400 hover:text-white transition-colors" data-testid="org-nav-login">Login</Link>
                    <Link to="/login" className="bg-white text-black px-5 py-2 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors" data-testid="org-nav-request-demo">Request Demo</Link>
                </div>
            </div>
        </nav>
    );
}

// ── Hero ──
function Hero() {
    return (
        <section className="relative min-h-[88vh] flex items-center overflow-hidden pt-24 pb-16" data-testid="org-hero">
            {/* Background photo + gradient */}
            <div className="absolute inset-0 z-0">
                <img src={IMG.hero} alt="A diverse enterprise team collaborating around a large screen in a dark modern office at dusk." className="w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7 space-y-7">
                    <Reveal>
                        <div className="inline-flex items-center gap-2 border border-white/[0.12] bg-white/[0.03] backdrop-blur-sm px-4 py-1.5 rounded-full" data-testid="org-hero-eyebrow">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#60A5FA] animate-pulse" />
                            <span className="text-[12px] uppercase tracking-[0.18em] font-mono text-gray-300">Realloc for Business</span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h1 className="font-display font-semibold text-white text-[44px] sm:text-[60px] lg:text-[80px] leading-[1.02] tracking-tight" data-testid="org-hero-h1">
                            <span className="block">Make your organization</span>
                            <span className="block italic text-gray-400">AI-native.</span>
                        </h1>
                    </Reveal>
                </div>
                <div className="lg:col-span-5 space-y-7">
                    <Reveal delay={0.1}>
                        <p className="text-[17px] lg:text-[20px] text-gray-400 leading-[1.55] max-w-md">
                            Only 10% of HR and L&D leaders believe their teams have the AI skills they need. Realloc closes that gap — training your people, building your workflows, and placing AI-native operators on your team.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/login" className="group inline-flex items-center gap-2 bg-white text-black px-7 py-3 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors" data-testid="org-hero-explore-btn">
                                Explore business solutions
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link to="/login" className="inline-flex items-center gap-2 border border-white/[0.15] text-gray-300 px-7 py-3 rounded-full text-sm font-medium hover:border-white/35 hover:text-white transition-colors" data-testid="org-hero-strategy-btn">
                                Book a strategy call
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

// ── Pillars ──
function Pillars() {
    return (
        <section className="py-32 sm:py-20 bg-background" data-testid="org-pillars">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                {PILLARS.map((p, i) => (
                    <Reveal key={p.num} delay={i * 0.05}>
                        <a href="#" className="group block border-t border-white/[0.08] hover:bg-white/[0.02] transition-colors" data-testid={`org-pillar-${p.num}`}>
                            <div className="grid grid-cols-12 gap-6 py-10 items-start">
                                <div className="col-span-2 sm:col-span-1 text-xs font-mono text-gray-600 tracking-wider pt-2">{p.num}</div>
                                <div className="col-span-10 sm:col-span-3 font-display text-[28px] sm:text-[40px] font-medium text-white leading-tight italic">{p.label}</div>
                                <div className="col-span-12 sm:col-span-7 space-y-2 pt-1">
                                    <p className="text-[18px] text-white font-semibold">{p.title}</p>
                                    <p className="text-[15px] text-gray-400 leading-relaxed max-w-xl">{p.body}</p>
                                </div>
                                <div className="hidden sm:block col-span-1 text-right pt-2">
                                    <ArrowRight className="inline-block w-5 h-5 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
                                </div>
                            </div>
                        </a>
                    </Reveal>
                ))}
                <div className="border-t border-white/[0.08]" />
                <Reveal>
                    <p className="text-[13px] text-gray-600 text-center mt-10" data-testid="org-pillars-trust">
                        500+ trained AI experts · Trusted by teams at Google, JP Morgan, and Bain
                        <span className="ml-2 text-gray-700">[placeholder — swap with Realloc clients]</span>
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

// ── Logo strip ──
function LogoStrip() {
    return (
        <section className="py-16 bg-[#0A0A0A] border-y border-white/[0.06]" data-testid="org-logos">
            <div className="max-w-7xl mx-auto px-5 md:px-8 text-center">
                <p className="text-[12px] uppercase tracking-[0.18em] font-mono text-gray-600 mb-8">Join teams from</p>
                <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-5 opacity-70">
                    {LOGO_NAMES.map((name) => (
                        <span key={name} className="font-display text-[16px] text-gray-500 hover:text-gray-200 transition-colors tracking-tight" data-testid={`org-logo-${name.replace(/[^a-z0-9]+/gi, "-")}`}>
                            {name}
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Metrics ──
function MetricsBand() {
    return (
        <section className="py-24 bg-background" data-testid="org-metrics">
            <div className="max-w-3xl mx-auto px-5 md:px-8">
                <div className="bg-[#111111] border border-white/[0.08] rounded-2xl p-10 sm:p-14">
                    <div className="grid grid-cols-3 gap-6 text-center mb-10">
                        {[
                            { val: "29,822", label: "Reviews" },
                            { val: "4.99", label: "Avg rating" },
                            { val: "2,025", label: "Submitted last month" },
                        ].map((s) => (
                            <div key={s.label}>
                                <p className="font-display text-[36px] sm:text-[56px] text-white leading-none font-semibold" data-testid={`org-metric-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>{s.val}</p>
                                <p className="text-[12px] uppercase tracking-wider font-mono text-gray-500 mt-3">{s.label}</p>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-5">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
                        ))}
                    </div>
                    <div className="flex items-center justify-center gap-4">
                        <div className="flex -space-x-3">
                            {IMG.avatars.map((src, i) => (
                                <img key={i} src={src} alt="Reviewer headshot" className="w-10 h-10 rounded-full ring-2 ring-white object-cover" />
                            ))}
                        </div>
                        <p className="text-[13px] text-gray-400">Trusted by leaders at <span className="text-white">enterprises worldwide</span></p>
                    </div>
                    <p className="text-[11px] text-gray-700 text-center mt-6 font-mono">[stats placeholder — replace with Realloc enterprise numbers]</p>
                </div>
            </div>
        </section>
    );
}

// ── Programs accordion + Live program card ──
function Programs() {
    const [open, setOpen] = useState(0);
    return (
        <section className="py-32 sm:py-20 bg-background" data-testid="org-programs">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <Reveal>
                    <h2 className="font-display text-[34px] sm:text-[56px] font-semibold text-white leading-[1.05] tracking-tight text-center max-w-3xl mx-auto mb-16" data-testid="org-programs-h2">
                        Everything you need to make your team <span className="italic text-gray-400">AI-native.</span>
                    </h2>
                </Reveal>
                <div className="grid lg:grid-cols-2 gap-12 items-start">
                    <div className="space-y-1">
                        {ACCORDION.map((a, i) => {
                            const isOpen = open === i;
                            return (
                                <div key={i} className="border-b border-white/[0.08]" data-testid={`org-accordion-${i}`}>
                                    <button
                                        onClick={() => setOpen(isOpen ? -1 : i)}
                                        className="w-full py-5 flex items-center justify-between text-left group"
                                        aria-expanded={isOpen}
                                    >
                                        <span className="text-[17px] font-semibold text-white">{a.title}</span>
                                        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    <div className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"}`}>
                                        <div className="overflow-hidden">
                                            <p className="text-[15px] text-gray-400 leading-relaxed pr-8">{a.body}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="pt-6">
                            <Link to="/login" className="inline-flex items-center gap-2 border border-white/[0.15] text-gray-300 px-5 py-2 rounded-full text-sm font-medium hover:border-white/35 hover:text-white transition-colors" data-testid="org-browse-programs-btn">
                                Browse programs
                                <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>

                    {/* Live program visual */}
                    <Reveal delay={0.1}>
                        <div className="relative">
                            <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-white/[0.08] relative">
                                <img src={IMG.presenter} alt="An instructor presenting to camera during a live Realloc program." className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                            </div>
                            {/* Floating cards */}
                            <div className="absolute -bottom-6 left-4 right-4 space-y-2">
                                <EventCard date="Jul 22" title="Kickoff" sub="Happening now · Started 4:00 PM · 45 min" cta="Join" live testid="org-event-live" />
                                <EventCard date="Jul 23" title="Understanding the Basics" sub="Tomorrow at 4:00 PM · 45 min" cta="Starts in 1d" testid="org-event-next" />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function EventCard({ date, title, sub, cta, live, testid }) {
    return (
        <div className="bg-white text-black rounded-xl px-4 py-3 flex items-center gap-4 shadow-2xl" data-testid={testid}>
            <div className="text-center w-12 shrink-0">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">{date.split(" ")[0]}</p>
                <p className="text-lg font-display font-semibold">{date.split(" ")[1]}</p>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{title}</p>
                <p className="text-[12px] text-gray-500 truncate">{sub}</p>
            </div>
            <button className={`px-3 py-1.5 rounded-full text-xs font-semibold ${live ? "bg-[#F59E0B] text-black flex items-center gap-1.5" : "bg-gray-100 text-gray-600"}`}>
                {live && <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />}
                {cta}
            </button>
        </div>
    );
}

// ── Capabilities grid ──
function Capabilities() {
    return (
        <section className="py-32 sm:py-20 bg-[#0A0A0A]" data-testid="org-capabilities">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <Reveal>
                    <div className="flex items-baseline justify-between mb-10">
                        <p className="text-[12px] uppercase tracking-[0.18em] font-mono text-gray-500 max-w-md">Realloc for teams · Train your people, deploy AI, and hire AI-native talent.</p>
                        <Link to="/login" className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1" data-testid="org-capabilities-learn-more">
                            Learn more <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                </Reveal>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {CAPABILITIES.map(({ Icon, label }, i) => (
                        <Reveal key={label} delay={i * 0.04}>
                            <div className="group bg-[#111111] border border-white/[0.08] rounded-2xl p-6 h-44 flex flex-col justify-between hover:border-white/[0.18] hover:-translate-y-0.5 transition-all cursor-pointer" data-testid={`org-capability-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                                <Icon className="w-7 h-7 text-gray-300 group-hover:text-white transition-colors" strokeWidth={1.5} />
                                <p className="text-[17px] font-semibold text-white">{label}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Mission ──
function Mission() {
    return (
        <section className="relative min-h-[90vh] flex items-end overflow-hidden" data-testid="org-mission">
            <div className="absolute inset-0">
                <img src={IMG.mission} alt="A confident professional walking through a city at golden hour, looking ahead." className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-black/20" />
            </div>
            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pb-20 w-full grid lg:grid-cols-2 gap-10 items-end">
                <Reveal>
                    <h2 className="font-display text-[40px] sm:text-[72px] font-semibold text-white leading-[1.05] tracking-tight" data-testid="org-mission-h2">
                        Ambition lives <span className="italic text-gray-300">here.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="text-[17px] sm:text-[19px] text-gray-300 leading-[1.55] max-w-md">
                        Realloc exists to honor ambition and help more people realize it. With the right experts and the right community, opportunity stops being a privilege and starts becoming a plan.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

// ── Portrait trio ──
function PortraitTrio() {
    const portraits = [
        { src: IMG.portrait1, alt: "A young professional in a navy shirt looking ahead optimistically." },
        { src: IMG.portrait2, alt: "A mid-career professional in an olive blazer at golden hour." },
        { src: IMG.portrait3, alt: "A young professional with glasses and a denim jacket on a campus." },
    ];
    return (
        <section className="py-32 sm:py-20 bg-background" data-testid="org-portraits">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <Reveal>
                    <h2 className="font-display text-center text-[34px] sm:text-[56px] font-semibold text-white leading-[1.05] tracking-tight max-w-3xl mx-auto" data-testid="org-portraits-h2">
                        The world is changing fast.
                        <span className="block italic text-gray-400">Realloc helps you change with it.</span>
                    </h2>
                    <p className="text-center text-[16px] text-gray-400 leading-relaxed max-w-xl mx-auto mt-5">
                        AI is rapidly changing how careers and businesses are built. No matter what your goals are, Realloc is here to help.
                    </p>
                </Reveal>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-14">
                    {portraits.map((p, i) => (
                        <Reveal key={i} delay={i * 0.06}>
                            <div className="aspect-[3/4] overflow-hidden rounded-2xl border border-white/[0.08] relative" data-testid={`org-portrait-${i}`}>
                                <img src={p.src} alt={p.alt} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── CTA band ──
function CtaBand() {
    return (
        <section className="py-24 bg-[#0A0A0A] border-y border-white/[0.06]" data-testid="org-cta-band">
            <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
                <h2 className="font-display text-[34px] sm:text-[56px] font-semibold text-white leading-[1.05] tracking-tight">
                    Take the next step toward
                    <span className="block">your goals</span>
                </h2>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                    <Link to="/login" className="bg-white text-black px-7 py-3 rounded-full text-sm font-semibold hover:bg-gray-200 transition-colors inline-flex items-center gap-2" data-testid="org-cta-get-started">
                        Get started
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/login" className="border border-white/[0.15] text-gray-300 px-7 py-3 rounded-full text-sm font-medium hover:border-white/35 hover:text-white transition-colors" data-testid="org-cta-request-demo">
                        Request Demo
                    </Link>
                </div>
            </div>
        </section>
    );
}

// ── Footer ──
function OrgFooter() {
    const cols = [
        { title: "Platform", links: ["Home", "Livestreams", "Live Programs", "Realloc+", "Become an expert"] },
        { title: "Popular", links: ["Build with AI", "Break into AI Careers", "MBA", "Management Consulting", "Product Management", "GRE", "Private Equity", "Career Coaching"] },
        { title: "Partnerships", links: ["Partner with Realloc", "For Universities", "AI Transformation", "Outplacement"] },
        { title: "Company", links: ["Reviews", "Careers", "Library"] },
        { title: "Resources", links: ["Support", "Legal", "Privacy"] },
    ];
    return (
        <footer className="bg-background border-t border-white/[0.06] py-16" data-testid="org-footer">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 mb-12">
                    <div className="col-span-2 sm:col-span-3 lg:col-span-1">
                        <Link to="/" className="font-display text-xl text-white">Realloc</Link>
                    </div>
                    {cols.map((c) => (
                        <div key={c.title}>
                            <p className="text-[11px] uppercase tracking-[0.18em] font-mono text-gray-500 mb-3">{c.title}</p>
                            <ul className="space-y-2">
                                {c.links.map((l) => (
                                    <li key={l}><a href="#" className="text-[13px] text-gray-400 hover:text-white transition-colors">{l}</a></li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
                <div className="flex items-center justify-between border-t border-white/[0.06] pt-6 text-[12px] text-gray-600 font-mono">
                    <span>Copyright 2026 Realloc Inc. All rights reserved.</span>
                    <div className="flex gap-4">
                        <a href="#" className="hover:text-white transition-colors">LinkedIn</a>
                        <a href="#" className="hover:text-white transition-colors">Instagram</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}
